<template>
  <!-- Modal selalu dimount dan hanya :show yang di-toggle, karena watcher show di Modal.vue hanya jalan saat nilainya berubah -->
  <modal
    :show="popupVisible"
    :showClose="false"
    :scrollToBottom="false"
    modalClasses="notice-popup"
    headerClasses="notice-popup__header"
    bodyClasses="notice-popup__body"
    footerClasses="notice-popup__footer"
  >
    <div slot="header" class="notice-popup__heading">
      <span v-if="currentNotice" class="notice-type-badge" :class="'notice-type-badge--' + currentNotice.type">
        {{ typeLabel(currentNotice.type) }}
      </span>
      <!-- tanpa title-up: judul tampil sesuai yang diketik root, sama seperti di bell -->
      <h4 class="title notice-popup__title">{{ currentNotice ? currentNotice.title : '' }}</h4>
    </div>

    <div v-if="currentNotice" class="notice-content" v-html="currentNotice.content"></div>

    <template slot="footer">
      <div class="notice-popup__actions">
        <span class="notice-popup__counter">
          <template v-if="popupQueue.length > 1">{{ popupIndex + 1 }} of {{ popupQueue.length }}</template>
        </span>
        <!-- warna tetap, bukan base-button: warna primary tema berbeda di setiap agency white-label -->
        <div class="notice-popup__buttons">
          <button v-if="popupQueue.length > 1" type="button" class="notice-popup__next" @click="nextPopup">Next</button>
          <button type="button" class="notice-popup__btn" @click="snoozeCurrent">Remind me later</button>
          <el-popover trigger="hover" :disabled="!isViewMode" content="Dismiss is not available in view-as mode" placement="top">
            <!-- span membuat keterangan tetap muncul walaupun tombolnya disabled -->
            <span slot="reference">
              <button
                type="button"
                class="notice-popup__btn notice-popup__btn--primary"
                :disabled="isViewMode || dismissing"
                @click="dismissCurrent"
              >Dismiss</button>
            </span>
          </el-popover>
        </div>
      </div>
    </template>
  </modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { Modal } from '@/components';
import {
  isNoticeAudience,
  isNoticeSnoozed,
  snoozeNotice,
  cleanupExpiredNoticeSnoozes,
  noticeTypeLabel,
} from '@/helpers/notices';
import './notices.css';

// Pindah page dan kembali ke tab berbagi satu batas: maksimal 1 request per menit (Keputusan 7)
const REFRESH_THROTTLE_MS = 60 * 1000;

/**
 * TASK-EMM-1020: pengambilan data notice dan popup Critical. Dimount sekali di DashboardNavbar
 * (navbar tidak ikut dimuat ulang saat pindah page). Tampilan bell ada di NoticeBell.vue.
 */
export default {
  name: 'NoticeCenter',
  components: {
    Modal,
  },
  data() {
    return {
      lastFetchAt: 0,
      fetching: false,
      popupVisible: false,
      popupIndex: 0,
      // localStorage tidak reaktif, jadi status snooze disalin ke sini setiap selesai mengambil data
      snoozedIds: {},
      dismissing: false,
    };
  },
  computed: {
    ...mapGetters(['visibleNotices', 'noticesUnavailable']),
    isAudience() {
      return isNoticeAudience(this.$global);
    },
    isViewMode() {
      return this.$global.globalviewmode === true;
    },
    viewedUserId() {
      const user = this.$store.getters.userData;
      return user && user.id ? user.id : null;
    },
    popupQueue() {
      return this.visibleNotices.filter(notice => notice.type === 'critical' && !this.snoozedIds[notice.id]);
    },
    currentNotice() {
      return this.popupQueue[this.popupIndex] || null;
    },
  },
  watch: {
    $route() {
      this.fetchNotices(false);
    },
    // jaga-jaga kalau flag $global baru terisi setelah navbar dimount
    isAudience(value) {
      if (value && !this.lastFetchAt) {
        this.fetchNotices(true);
      }
    },
    popupQueue(queue) {
      if (!queue.length) {
        this.popupVisible = false;
        this.popupIndex = 0;
      } else if (this.popupIndex >= queue.length) {
        this.popupIndex = 0;
      }
    },
  },
  methods: {
    typeLabel(type) {
      return noticeTypeLabel(type);
    },
    async fetchNotices(force) {
      if (!this.isAudience || this.noticesUnavailable || this.fetching) return;
      if (!force && Date.now() - this.lastFetchAt < REFRESH_THROTTLE_MS) return;

      this.fetching = true;
      this.lastFetchAt = Date.now();
      try {
        await this.$store.dispatch('fetchVisibleNotices', {
          viewUserId: this.isViewMode ? this.viewedUserId : null,
        });
        this.refreshSnoozed();
        this.openPopupIfNeeded();
      } catch (error) {
        if (error && error.response && error.response.status === 403) {
          // bukan audience notice (misalnya sales yang sedang view-as): sembunyikan bell tanpa toast dan tanpa retry
          this.$store.commit('setVisibleNotices', []);
          this.$store.commit('setNoticesUnavailable', true);
        }
        // error lain didiamkan; dicoba lagi di trigger berikutnya
      } finally {
        this.fetching = false;
      }
    },
    refreshSnoozed() {
      const snoozed = {};
      this.visibleNotices.forEach(notice => {
        if (isNoticeSnoozed(this.viewedUserId, notice.id)) {
          snoozed[notice.id] = true;
        }
      });
      this.snoozedIds = snoozed;
    },
    openPopupIfNeeded() {
      if (this.popupQueue.length && !this.popupVisible) {
        this.popupIndex = 0;
        this.popupVisible = true;
      }
    },
    nextPopup() {
      if (this.popupQueue.length) {
        this.popupIndex = (this.popupIndex + 1) % this.popupQueue.length;
      }
    },
    snoozeCurrent() {
      const notice = this.currentNotice;
      if (!notice) return;
      snoozeNotice(this.viewedUserId, notice.id);
      this.snoozedIds = Object.assign({}, this.snoozedIds, { [notice.id]: true });
    },
    async dismissCurrent() {
      const notice = this.currentNotice;
      if (!notice || this.isViewMode || this.dismissing) return;

      this.dismissing = true;
      try {
        await this.$store.dispatch('dismissNotice', notice.id);
      } catch (error) {
        this.$notify({ type: 'danger', message: 'Failed to dismiss notice. Please try again.', icon: 'fas fa-bug' });
        this.fetchNotices(true);
      } finally {
        this.dismissing = false;
      }
    },
    onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.fetchNotices(false);
      }
    },
    onWindowFocus() {
      this.fetchNotices(false);
    },
  },
  mounted() {
    // pembersihan key kedaluwarsa hanya saat halaman dimuat penuh (Keputusan 6)
    cleanupExpiredNoticeSnoozes();
    this.fetchNotices(true);
    // visibilitychange: pindah tab atau minimize; focus: Alt+Tab dari aplikasi lain
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('focus', this.onWindowFocus);
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('focus', this.onWindowFocus);
  },
};
</script>

<style>
.notice-popup {
  max-width: 560px;
}
/* garis tipis memisahkan judul dari isi, sama seperti panel bell */
.notice-popup .notice-popup__header {
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}
.notice-popup__heading {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
}
/* judul selalu lebih besar dari Heading 1 di konten (18px) */
.notice-popup .notice-popup__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  text-align: left;
  word-break: normal;
  overflow-wrap: anywhere;
}
/* modal-open di body bisa hilang saat navigasi, jadi isi popup punya scroll sendiri */
.notice-popup .notice-popup__body {
  max-height: 55vh;
  overflow-y: auto;
  color: #222a42;
}
.notice-popup .notice-popup__footer {
  padding: 12px 24px 20px;
}
.notice-popup__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
}
.notice-popup__counter {
  font-size: 13px;
  color: #6b7280;
}
.notice-popup__buttons {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
/* Remind me later: outline putih, sama dengan tombol Dismiss di bell */
.notice-popup .notice-popup__btn {
  margin: 0;
  padding: 6px 14px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #fff;
  color: #222a42;
  font-size: 13px;
  font-weight: 600;
  text-transform: none;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.notice-popup .notice-popup__btn:hover {
  background: #f5f7fa;
  border-color: #c0c4cc;
}
/* Dismiss: aksi utama, solid navy */
.notice-popup .notice-popup__btn--primary {
  border-color: #222a42;
  background: #222a42;
  color: #fff;
}
.notice-popup .notice-popup__btn--primary:hover {
  border-color: #344054;
  background: #344054;
}
.notice-popup .notice-popup__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
/* Next: tautan teks, seperti "Dismiss all" di bell */
.notice-popup .notice-popup__next {
  margin: 0;
  padding: 0 4px;
  border: 0;
  background: none;
  color: #222a42;
  font-size: 13px;
  font-weight: 600;
  text-decoration: underline;
  text-transform: none;
  cursor: pointer;
}
</style>
